import { PageMeta } from 'constants/pages';
import { EHashtag, TCardProps } from 'types/common';

import { ArticleSections } from 'types/articles';

import CollaborationImage from 'assets/partnerships/wave-sense/collaboration.webp';
import UpdatedCollaborationImage from 'assets/partnerships/wave-sense/updated-collaboration.webp';

import ObjectsRecognitionsImage from 'assets/partnerships/wave-sense/objects-recognitions.webp';
import VisitCardImage from 'assets/partnerships/wave-sense/visit-card.webp';

const title = 'AILiveSim & WaveSense: Partnering to Power All-Weather Autonomous Navigation';

export const waveSensePartnershipMeta: PageMeta = {
  url: '/company-updates/wave-sense',
  title,
  description:
    'AILiveSim and WaveSense Dresden GmbH partner to revolutionize autonomous navigation. Combining high-fidelity simulation with advanced radar algorithms, the collaboration enables faster development, resilient validation, and safer all-weather autonomous systems.',
};

export const waveSensePartnershipCard: TCardProps = {
  title,
  description: `
    AILiveSim and WaveSense Dresden GmbH join forces to 
    accelerate and validate all-weather autonomous navigation, 
    fusing radar expertise with AI-driven simulation for safer, 
    more resilient systems.
  `,
  category: 'company-updates',
  type: 'Company Updates',
  image: UpdatedCollaborationImage,
  postUrl: waveSensePartnershipMeta.url,
  publishDate: 'September 17, 2025',
  needImageBg: true,
  hashTags: [EHashtag.AutonomousVehicles, EHashtag.SafetyValidation, EHashtag.MaritimeTechnology],
};

export const waveSensePartnership: ArticleSections[] = [
  {
    type: 'header',
    resourceType: 'Company Updates',
    title,
    text: (
      <p>
        We are extremely proud to announce that <strong>AILiveSim Oy</strong> has joined forces with{' '}
        <strong>WaveSense Dresden GmbH</strong> in a strategic collaboration set to revolutionize
        the development and validation of autonomous navigation software. This powerful partnership
        will fuse AILiveSim&apos;s <strong>high-fidelity simulation capabilities</strong> with
        WaveSense Dresden GmbH’s{' '}
        <strong>deep expertise in innovative radar algorithms and software solutions</strong>,
        creating an unparalleled offering for building and testing resilient, all-weather autonomous
        systems.
      </p>
    ),
    image: CollaborationImage,
  },
  {
    type: 'common',
    title: '',
    text: (
      <>
        <p>This collaboration brings together the best of both worlds:</p>

        <ul>
          <li>
            <strong>Accelerated Development:</strong> By integrating WaveSense Dresden GmbH&apos;s
            radar algorithm models and data into AILiveSim&apos;s simulation environment, developers
            can now design, test, and refine autonomous navigation algorithms that leverage advanced
            radar capabilities from the very beginning. This significantly reduces the need for
            extensive real-world testing in challenging conditions, speeding up the development
            cycle.
          </li>

          <li style={{ margin: '16px 0' }}>
            <strong>Enhanced Validation:</strong> The combined solution will enable comprehensive
            validation of autonomous systems under a wide array of simulated weather conditions and
            environmental challenges, ensuring that the navigation software is truly resilient and
            reliable.
          </li>

          <li>
            <strong>Safer Autonomous Systems:</strong> Ultimately, this partnership will contribute
            to the creation of safer and more dependable autonomous vehicles and robots by equipping
            them with navigation capabilities that can withstand even the harshest environments.
          </li>
        </ul>

        <p>
          &quot;We are incredibly excited about this partnership with WaveSense Dresden GmbH,&quot;
          says Jerome Leudet, Founder at AILiveSim Oy. &quot;Their groundbreaking expertise in radar
          algorithms perfectly complements our high performance simulation platform. Together, we
          can provide developers with the tools they need to build trustworthy autonomous
          navigation, faster and more effectively.&quot;
        </p>

        <p>
          &quot;Dr. Reinhold Herschel, founder of WaveSense Dresden GmbH&quot; adds, &quot;Joining
          forces with AILiveSim Oy allows us to extend the reach of our radar software solutions
          into the simulation domain, empowering engineers to build and test autonomous systems with
          an unprecedented level of resilience. This collaboration marks a significant step forward
          in making autonomous navigation robust in all conditions.&quot;
        </p>
      </>
    ),
  },
  {
    type: 'common',
    title:
      'AILiveSim: The simulation engine that turns weeks of data wrangling into same-day insights',
    text: (
      <>
        <p>
          AILiveSim is a cutting-edge simulation platform designed to revolutionize MLOps timelines.
          It transforms time-consuming data collection and labeling into an automated, end-to-end
          pipeline, directly streaming fully labeled sensor output into model-training workflows.
          This significantly reduces MLOps timelines from weeks to days only.
        </p>

        <p>
          At its core, AILiveSim features a cloud-native &quot;Intelligent Training Pipeline&quot;
          built around a state-of-the-art Unreal-Engine. This powerful combination streamlines
          simulation, enabling teams to obtain fully labeled, radar-rich datasets without the need
          for manual filtering or annotation.
        </p>

        <p>
          AILiveSim&apos;s physics-based models provide millimeter-precise outputs for camera,
          lidar, thermal, and radar sensors, guaranteeing sensor simulation of unprecedented
          quality. This quality is maintained even with constant platform updates. Developers have
          complete control, able to parametrize every variable including weather, terrain, vehicle
          dynamics, or pedestrian behavior. These permutations allow for the discovery of long-tail
          failure modes through or edge cases otherwise difficult, expensive and risky to reproduce
        </p>

        <p>
          Already proven across land, sea, and air programs, AILiveSim dramatically shortens MLOps
          cycles, reduces prototype costs, and empowers innovators to validate safety-critical
          autonomy with the high fidelity and speed required by today’s robotics and mobility
          leaders.
        </p>
      </>
    ),
    image: ObjectsRecognitionsImage,
    submenu: [
      {
        title: '(WaveSense) : Expertise in Radar technologies and software solutions.',
        text: (
          <>
            <p>
              WaveSense Dresden GmbH, an innovative software provider from Dresden, specializes in
              developing{' '}
              <strong>customized software modules for processing and analyzing radar data</strong>.{' '}
              As a pure software company, they collaborate with various sensor manufacturers to
              create tailored solutions that enhance the reliability and precision of radar-based
              perception. Their expertise includes hardware-independent radar knowledge, generation
              of synthetic radar data using advanced 3D ray tracers, and the development of
              simulation modules and tools.
            </p>

            <p>
              Find out some more about wavesense{' '}
              <a href="https://wavesense-dd.de/" target="_blank" rel="noopener noreferrer">
                https://wavesense-dd.de/
              </a>
              .
            </p>
          </>
        ),
        type: 'common',
        image: VisitCardImage,
      },
    ],
  },
  {
    type: 'common',
    title: 'Way forward together',
    text: (
      <>
        <p>
          This strategic partnership between AILiveSim Oy and WaveSense Dresden GmbH is a testament
          to our shared commitment to advancing autonomous technology. We believe that by combining
          our strengths, we will play a pivotal role in accelerating the deployment of safe,
          reliable, and truly all-weather autonomous solutions across various industries.
        </p>

        <p>
          Stay tuned for more updates as we embark on this exciting journey to shape the future of
          autonomous navigation!
        </p>
      </>
    ),
  },
];
